import { EventEmitter, Injectable } from '@angular/core';
import { CookieService } from 'ngx-cookie-service';
import { PlayerRepository } from '../infrastructure/repositories/player.repository';
import { PlayerValidationResponse } from '../model/interfaces/player-validation-response';
import { VisitorPreferences } from '../model/interfaces/visitor-preferences';
import { SimplePlayer } from '../model/simple-player';
import { Visitor } from '../model/visitor';
import { CprService } from './cpr.service';
import { StorageService } from './storage.service';

@Injectable({
  providedIn: 'root'
})
export class VisitorService
{
    public onVisitorCreated = new EventEmitter<Visitor>();

    private currentVisitor: Visitor;
    private visitorStorageKey = 'shop.player';
    private visitorIsActiveKey = 'shop.player.active';
    private visitorIsActive: boolean | null = null;
    private preferences: VisitorPreferences;

    protected readonly playerRepository: PlayerRepository;
    private readonly storageService: StorageService;
    private readonly cookieService: CookieService;
    private readonly cprService: CprService;

    public constructor(
        cookieService: CookieService,
        playerRepository: PlayerRepository,
        storageService: StorageService,
        cprService: CprService)
    {
        this.cookieService = cookieService;
        this.playerRepository = playerRepository;
        this.storageService = storageService;
        this.cprService = cprService;
    }

    public getVisitor(): Visitor
    {
        if (this.currentVisitor == null)
        {
            const storedVisitor = this.getVisitorFromStorage();
            this.currentVisitor = storedVisitor || null;
        }

        return Object.assign({}, this.currentVisitor || (this.currentVisitor = {}));
    }

    public getPreferences(): VisitorPreferences
    {
        if (this.preferences != null)
        {
            return { ...this.preferences };
        }

        this.preferences = {};
        return { ...this.preferences };
    }

    public setPreferences(preferences: VisitorPreferences): void
    {
        this.preferences = { ...preferences };
    }

    public forgetPreferences(): void
    {
        this.preferences = null;
    }

    public forgetVisitor(): void
    {
        this.currentVisitor = null;
        this.storageService.removeItem(this.visitorStorageKey);
        this.storageService.setItem(this.visitorIsActiveKey, (this.visitorIsActive = false));
        this.forgetPreferences();
    }

    public commitVisitor(visitor: Visitor): void
    {
        this.currentVisitor = Object.assign({}, visitor);
        // notify listeners
        this.onVisitorCreated.emit(this.currentVisitor);
        this.storageService.setItem(this.visitorStorageKey, visitor);
        this.storageService.setItem(this.visitorIsActiveKey, (this.visitorIsActive = true));
    }

    public isVisitorActive(): boolean
    {
        let activeState = this.visitorIsActive;
        if (activeState != null)
        {
            return activeState;
        }

        activeState = this.storageService.getItem<boolean>(this.visitorIsActiveKey);
        if (activeState == null)
        {
            activeState = false;
            this.storageService.setItem(this.visitorIsActiveKey, activeState);
        }

        return activeState;
    }

    public async validateVisitor(visitor: Visitor): Promise<PlayerValidationResponse>
    {
        const player = <SimplePlayer>visitor;
        return this.playerRepository.validatePlayer(player);
    }
    
    public registerCustomer(): void
    {
        this.cookieService.set('Kunde', '1', 365, '/', '.klasselotteriet.dk');
    }

    private getVisitorFromStorage(): Visitor
    {
        const visitor = this.storageService.getItem<Visitor>(this.visitorStorageKey);
        if (visitor != null && visitor.DateOfBirth != null)
        {
            visitor.DateOfBirth = new Date(visitor.DateOfBirth);
        }

        return visitor;
    }

  /**
   * Masks the check digits of the CPR number in the given Visitor object for privacy.
   * 
   * This method clones the provided Visitor object and replaces the check digits
   * of the CPR number (after the hyphen) with 'xxxx', preserving only the date of birth part.
   * If the Visitor does not have a CPR number, the object is returned unchanged.
   * 
   * @param visitor - The Visitor object whose CPR number should be masked.
   * @returns A new Visitor object with the CPR number masked, or the original if no CPR is present.
 */  
  public maskCprCheckDigits(visitor: Visitor): Visitor {
    return visitor;
    const visitorCloned = { ...visitor };
    
    if (visitorCloned.Cpr) {
      const { dob } = this.cprService.separateCpr(visitorCloned.Cpr);
      visitorCloned.Cpr = `${dob}-xxxx`;
    }

    return visitorCloned;
  }
}
